import React, { useCallback, useEffect, useState } from 'react';
import { useDidUpdate } from 'rooks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import pluralize from 'pluralize';
import { Button, Spin, Table } from 'antd';
import { Select } from 'antd';
import moment from 'moment';
import cx from 'classnames';

import {
  getAssignmentsTableColumns,
  getAssignmentTypeFilterLabel,
  getAssignmentTypeLabel,
} from 'utils/assignmentUtils';
import {
  AssignmentTypeFilterEnum,
  SummaryAssignmentDetailModel,
  SummaryAssignmentDetailResponseModel,
} from 'api/backofficeApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { AssignmentInfoTypes } from 'features/assignment/enums';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import { getSelectedFilterValues } from 'utils/filterUtils';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { typeConversionToNumberArray } from 'utils/convertUtils';
import { isCorporateTASelector } from 'redux/selectors/authenticationSelectors';
import {
  getMyActiveAssignmentsDetail,
  getMyActiveAssignmentsDetailExcel,
} from 'redux/reducers/homeDashboardSlice';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './MyActiveAssignmentsDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';
const columns = [
  {
    title: 'Assignment',
    key: 'assignments',
    render: (record: SummaryAssignmentDetailModel) => (
      <Link
        to={`/assignment/${record.assignmentKey}?section=${AssignmentInfoTypes.Primary}`}
      >
        {record.assignmentKey}
      </Link>
    ),
  },
  {
    title: 'Assignment type',
    key: 'assignmentType',
    render: (record: SummaryAssignmentDetailModel) =>
      getAssignmentTypeLabel(record.assignmentType),
  },
  {
    title: 'Customer',
    key: 'customer',
    render: (record: SummaryAssignmentDetailModel) => (
      <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
        {record.customerName}
      </Link>
    ),
  },
  {
    title: 'Customer ID',
    key: 'customerId',
    render: (record: SummaryAssignmentDetailModel) => record.customerKey,
  },
  {
    title: 'Job title',
    key: 'jobTitle',
    render: (record: SummaryAssignmentDetailModel) => record.jobTitle,
  },
  {
    title: 'Start date',
    key: 'startDate',
    render: (record: SummaryAssignmentDetailModel) =>
      moment.utc(record.jobStartDate).format('MM/DD/YYYY'),
  },
  {
    title: 'End date',
    key: 'endDate',
    render: (record: SummaryAssignmentDetailModel) =>
      moment.utc(record.jobEndDate).format('MM/DD/YYYY'),
  },
];

const assignmentFilterTypes = [
  AssignmentTypeFilterEnum.All,
  AssignmentTypeFilterEnum.W2Assignment,
  AssignmentTypeFilterEnum.ICAssignment,
  AssignmentTypeFilterEnum.SCAssignment,
];

export default function MyActiveAssignmentsDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { assignmentTypes } = queryString.parse(location.search);
  const dispatch = useAppDispatch();
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isActiveAssignmentsDataLoading, setIsActiveAssignmentsDataLoading] =
    useState(false);
  const [activeAssignmentsData, setActiveAssignmentsData] =
    useState<SummaryAssignmentDetailResponseModel>();
  const [selectedTypes, setSelectedTypes] = useState<
    AssignmentTypeFilterEnum[]
  >(typeConversionToNumberArray(assignmentTypes));
  const [currentTablePage, setCurrentTablePage] = useState(1);

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getMyActiveAssignmentsDetailExcel(selectedTypes),
    );

    if (getMyActiveAssignmentsDetailExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const tableColumns = getAssignmentsTableColumns(columns, isCorporateTA);

  const assignmentsCountMessage = pluralize(
    'assignment',
    activeAssignmentsData?.totalCount,
    true,
  );

  useDidUpdate(() => {
    const queryData = {
      assignmentTypes: selectedTypes,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [selectedTypes]);

  const loadActiveAssignmentsDetails = useCallback(async () => {
    setIsActiveAssignmentsDataLoading(true);

    const result = await dispatch(
      getMyActiveAssignmentsDetail({
        page: currentTablePage - 1,
        assignmentTypes: selectedTypes,
      }),
    );

    if (getMyActiveAssignmentsDetail.fulfilled.match(result)) {
      setActiveAssignmentsData(result.payload);
    } else {
      setActiveAssignmentsData({
        totalCount: 0,
        details: [],
      });
    }

    setIsActiveAssignmentsDataLoading(false);
  }, [dispatch, selectedTypes, currentTablePage]);

  const handleAssignmentTypesSelection = (
    types: AssignmentTypeFilterEnum[],
  ) => {
    const selectedFilterTypes = getSelectedFilterValues(
      types,
      AssignmentTypeFilterEnum.All,
    );

    setSelectedTypes(selectedFilterTypes);
    setCurrentTablePage(1);
  };

  const loadInitialData = useCallback(async () => {
    await loadActiveAssignmentsDetails();

    setIsInitialDataLoaded(true);
  }, [loadActiveAssignmentsDetails]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details" />

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={assignmentsCountMessage}
              title="My active assignments"
            >
              <div className={commonStyles.selectBox}>
                <Select
                  mode="multiple"
                  showArrow
                  allowClear
                  showSearch={false}
                  placeholder="Select assignment type"
                  style={{ width: '100%' }}
                  onChange={handleAssignmentTypesSelection}
                  value={selectedTypes}
                >
                  {assignmentFilterTypes.map((type) => (
                    <Select.Option key={`select-type-${type}`} value={type}>
                      {getAssignmentTypeFilterLabel(type)}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>

            {!isInitialDataLoaded && <Spin />}
            {isInitialDataLoaded && (
              <Table
                className={cx('custom-table', styles.table)}
                pagination={{
                  total: activeAssignmentsData?.totalCount,
                  pageSize: 12,
                  onChange: setCurrentTablePage,
                  current: currentTablePage,
                  showSizeChanger: false,
                }}
                columns={tableColumns}
                dataSource={activeAssignmentsData?.details}
                rowKey={(record) => record?.assignmentId as string}
                loading={isActiveAssignmentsDataLoading}
                locale={{
                  emptyText: 'You currently do not have any assignments.',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
